import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter, HostListener,
  Input,
  OnDestroy, OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {fromEvent, Subscription} from 'rxjs';
import {debounceTime, map} from 'rxjs/operators';
import {EntitiesAutocompleteService} from "./entities-autocomplete.service";
import {
  EntitiesAutocompleteSearchType,
  EntitiesAutocompleteProfile,
  EntitiesAutocompleteReturnType,
  Entity
} from "./entities-autocomplete.model";
import {TranslateService} from "@ngx-translate/core";
import { ScreenType } from 'projects/common/model/ScreenType';
import { LandingModelService } from 'projects/common/services/landingModel.service';

@Component({
  selector: 'entities-autocomplete',
  template: `
    <div [ngClass]="{'expanded-mobile': isMobile && showMobileModal}">
      <i (click)="setShowMobileModal(false)" class="show-mobile-button eva-3-icon-arrow-bold-left -eva-3-icon-lg -eva-3-m-lg"></i>
      <p class="title-mobile -eva-3-bold" [innerHTML]="mobileTitle"></p>

      <div class="item-label eva-3-label-form -lg">
        <tooltip id="tooltip"
          [openOnClick]="true" [closeOnOutsideClick]="true" [closeOnInsideClick]="true"
          [classes]="'eva-3-tooltip--menu ' + (loading ? '' : '-scroll-y')"
          position="-bottom" backgroundColor="-white"
          product="nymeria-bo" flow="landing">

          <div label class="eva-3-input -lg -input-tag" [ngClass]="{'-invalid': invalid, '-icon-left': icon}">
            <div class="input-container">
              <input #input (click)="setShowMobileModal(true)" class="input-tag" type="text" [ngModel]="display" [placeholder]="placeholder"
                 (input)="inputHandler($event)" [disabled]="isDisabled" autocomplete="off">
              <i *ngIf="icon" class="eva-3-icon-{{icon}} input-icon"></i>
            </div>
          </div>

          <loader tooltip-content *ngIf="loading" product="nymeria-bo" flow="landing"></loader>
          <ul tooltip-content *ngIf="!loading" class="list tooltip-section-container">
            <li class="item eva-3-label-form -option-item -singleline -clickable" *ngFor="let city of suggestions"
                (click)="select(city)">
              <span>{{ city.display }}</span>
            </li>
            <li *ngIf="suggestions?.length === 0" class="item eva-3-label-form -option-item -singleline">
              <span>{{noSuggestionMessage}}</span>
            </li>
          </ul>

        </tooltip>
      </div>
    </div>
  `,
  styleUrls: ['./entities-autocomplete.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EntitiesAutocompleteComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() placeholder: string;
  @Input() search: EntitiesAutocompleteSearchType[];
  @Input() profile: EntitiesAutocompleteProfile = 'packages';
  @Input() exclude: Entity[] = [];
  @Input() invalid: boolean = false;
  @Input() isDisabled = false;
  @Input() showSelection = true; // sigue mostrando la ciudad una vez seleccionada
  @Input() locale: string = 'es_AR';
  @Input() icon?: string;
  @Input() mobileTitle?: string;

  // forma standard
  @Input() model: {};
  @Input() propertyName: string;
  @Input() returnType: EntitiesAutocompleteReturnType;
  @Output() isSelectedChange = new EventEmitter<boolean>();

  // para casos especiales
  @Input() selected: Entity;
  @Output() selectedChange = new EventEmitter<Entity>();

  suggestions: Entity[] = [];
  suggestions$: Subscription;
  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  showMobileModal = false;
  loading = false;
  display: string | null;
  gid: string = '';
  isSelected = false;
  isMobile: boolean;
  screenType: ScreenType;

  getNoSuggestionMessage = (hasValidHint: boolean) : string => {
    return this.translateService.instant('entities-autocomplete.' + (hasValidHint ? 'no-results' : 'empty-input'));
  }
  noSuggestionMessage: string = this.getNoSuggestionMessage(false);

  constructor(public entitiesAutocompleteService: EntitiesAutocompleteService,
              private translateService: TranslateService,
              private landingModelService: LandingModelService) {
  }

  ngOnInit(): void {
    this.screenType = this.landingModelService.getLandingModel().device.type;
    this.isMobile = this.screenType === ScreenType.MOBILE || globalThis.innerWidth <= 768;
    if (this.selected){
      this.select(this.selected);
    }
  }

  select(city: Entity) {
    this.showMobileModal = false;
    if (this.model && this.propertyName) {
      this.model[this.propertyName] = this.toExpectedType(city);
    }
    if (this.showSelection) {
      this.display = city.display;
      this.isSelected = true;
    } else {
      this.display = null;
      // @ts-ignore
      this.input.nativeElement.value = null;
    }
    this.setShowMobileModal(false)
    this.selectedChange.emit(city);
    this.isSelectedChange.emit(this.isSelected);

  }

  toExpectedType(city: Entity) {
    switch (this.returnType) {
      case "GID":
        return city.gid;
      case "OID":
        return city.gid.replace(/.*_/, '');
      case "CODE":
        return city.code;
      case "GID|CODE":
        return city.gid + "|" + (city.code || '');
    }
  }

  inputHandler(event: Event) {
    if (this.isSelected) {
      this.isSelected = false;
      this.display = event['data'];
      if (this.model?.[this.propertyName]) {
        this.model[this.propertyName] = null;
      }
      this.isSelectedChange.emit(false);
    }
  }

  ngAfterViewInit() {
    this.suggestions$ = fromEvent(this.input.nativeElement, 'input')
      .pipe(
        debounceTime(250),
        map((event: any) => event.target.value)
      )
      .subscribe(async (hint: string) => {
        const hasValidHint = hint.length >= 3;
        this.noSuggestionMessage = this.getNoSuggestionMessage(hasValidHint);
        if (!hasValidHint) {
          return;
        }
        this.showMobileModal = true;
        this.loading = true;
        const suggestions = await this.entitiesAutocompleteService.getSuggestions(hint, this.search.join(','), this.locale, this.profile);
        const exclusions = this.exclude.map(({gid}) => gid);
        this.suggestions = suggestions.filter(s => !exclusions.includes(s.gid));
        this.loading = false;
      });
  }

  ngOnDestroy() {
    this.suggestions$?.unsubscribe();
  }

  setShowMobileModal = (newValue: boolean) => {
    this.showMobileModal = newValue;
    if (this.isMobile){
      if (this.showMobileModal){
        globalThis.document.body.classList.add('nym-ac-lock-scroll');
      } else {
        globalThis.document.body.classList.remove('nym-ac-lock-scroll');
      }
    }
  }
}
